import { useState } from "react";
import {
  Package,
  Eye,
  EyeOff,
  Loader2,
  User,
  Phone,
  Briefcase,
  Mail,
  Lock,
  ShieldCheck,
} from "lucide-react";
import toast from "react-hot-toast";
import { useAuth } from "../hooks/useAuth";

const Login = () => {
  const [isLogin, setIsLogin] = useState(true);
  const [showPassword, setShowPassword] = useState(false);
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    password: "",
    password_confirmation: "",
    phone: "",
    role: "customer",
    address: "",
  });

  const { login, register, isLoading } = useAuth();

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (isLogin) {
      const result = await login({
        email: formData.email,
        password: formData.password,
      });
      if (result.success) {
        toast.success("Welcome back!");
        window.location.reload();
      } else {
        toast.error(result.error);
      }
    } else {
      if (formData.password !== formData.password_confirmation) {
        toast.error("Passwords do not match");
        return;
      }
      const result = await register(formData);
      if (result.success) {
        toast.success("Account created successfully!");
        window.location.reload();
      } else {
        toast.error(result.error);
      }
    }
  };

  const handleDemoFill = (email, password, role = "customer") => {
    setFormData((prev) => ({ ...prev, email, password, role }));
  };

  return (
    <div className="min-h-screen bg-[radial-gradient(ellipse_at_top_right,_var(--tw-gradient-stops))] from-indigo-50 via-slate-50 to-blue-100 flex items-center justify-center p-4 antialiased">
      <div className="w-full max-w-md my-8">
        <div className="text-center mb-8 group">
          <div className="inline-flex items-center justify-center w-20 h-20 rounded-2xl bg-indigo-600 shadow-lg shadow-indigo-200 mb-4 transform transition-all duration-300 group-hover:scale-105 group-hover:rotate-3">
            <img
              src="/quickdash_favicon_v2.png"
              alt="QuickDash"
              className="w-12 h-12 object-contain"
            />
          </div>
          <h1 className="text-3xl font-extrabold tracking-tight text-slate-950">
            QuickDash
          </h1>
          <p className="text-sm font-medium text-slate-500 mt-1.5">
            Fast Delivery Management
          </p>
        </div>

        <div className="bg-white/80 backdrop-blur-xl border border-slate-100 rounded-3xl shadow-xl shadow-slate-200/50 p-8 transition-all duration-300">
          <div className="mb-6">
            <h2 className="text-2xl font-bold text-slate-900">
              {isLogin ? "Welcome Back" : "Create Account"}
            </h2>
            <p className="text-xs text-slate-400 mt-1">
              {isLogin
                ? "Please enter your credentials to sign in"
                : "Get started by filling out your details"}
            </p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-4">
            {!isLogin && (
              <>
                <div>
                  <label className="block text-xs font-semibold uppercase tracking-wider text-slate-600 mb-1.5">
                    Full Name
                  </label>
                  <div className="relative">
                    <User className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                    <input
                      type="text"
                      name="name"
                      value={formData.name}
                      onChange={handleChange}
                      className="w-full pl-10 pr-4 py-2.5 bg-slate-50/50 border border-slate-200 rounded-xl text-sm focus:outline-none focus:border-indigo-500 focus:ring-4 focus:ring-indigo-100 transition-all text-slate-800 placeholder:text-slate-400"
                      placeholder="John Doe"
                      required={!isLogin}
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-xs font-semibold uppercase tracking-wider text-slate-600 mb-1.5">
                    Phone
                  </label>
                  <div className="relative">
                    <Phone className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                    <input
                      type="tel"
                      name="phone"
                      value={formData.phone}
                      onChange={handleChange}
                      className="w-full pl-10 pr-4 py-2.5 bg-slate-50/50 border border-slate-200 rounded-xl text-sm focus:outline-none focus:border-indigo-500 focus:ring-4 focus:ring-indigo-100 transition-all text-slate-800 placeholder:text-slate-400"
                      placeholder="+1 (234) 567-890"
                      required={!isLogin}
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-xs font-semibold uppercase tracking-wider text-slate-600 mb-1.5">
                    Account Type
                  </label>
                  <div className="relative">
                    <Briefcase className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                    <select
                      name="role"
                      value={formData.role}
                      onChange={handleChange}
                      className="w-full pl-10 pr-4 py-2.5 bg-slate-50/50 border border-slate-200 rounded-xl text-sm focus:outline-none focus:border-indigo-500 focus:ring-4 focus:ring-indigo-100 transition-all text-slate-800 appearance-none cursor-pointer"
                    >
                      <option value="customer">Customer</option>
                      <option value="courier">Courier</option>
                    </select>
                  </div>
                </div>
              </>
            )}

            <div>
              <label className="block text-xs font-semibold uppercase tracking-wider text-slate-600 mb-1.5">
                Email Address
              </label>
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                <input
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  className="w-full pl-10 pr-4 py-2.5 bg-slate-50/50 border border-slate-200 rounded-xl text-sm focus:outline-none focus:border-indigo-500 focus:ring-4 focus:ring-indigo-100 transition-all text-slate-800 placeholder:text-slate-400"
                  placeholder="you@example.com"
                  required
                />
              </div>
            </div>

            <div>
              <label className="block text-xs font-semibold uppercase tracking-wider text-slate-600 mb-1.5">
                Password
              </label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                <input
                  type={showPassword ? "text" : "password"}
                  name="password"
                  value={formData.password}
                  onChange={handleChange}
                  className="w-full pl-10 pr-10 py-2.5 bg-slate-50/50 border border-slate-200 rounded-xl text-sm focus:outline-none focus:border-indigo-500 focus:ring-4 focus:ring-indigo-100 transition-all text-slate-800 placeholder:text-slate-400"
                  placeholder="••••••••"
                  required
                  minLength={8}
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600 transition-colors focus:outline-none"
                >
                  {showPassword ? (
                    <EyeOff className="w-4 h-4" />
                  ) : (
                    <Eye className="w-4 h-4" />
                  )}
                </button>
              </div>
            </div>

            {!isLogin && (
              <div>
                <label className="block text-xs font-semibold uppercase tracking-wider text-slate-600 mb-1.5">
                  Confirm Password
                </label>
                <div className="relative">
                  <ShieldCheck className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                  <input
                    type="password"
                    name="password_confirmation"
                    value={formData.password_confirmation}
                    onChange={handleChange}
                    className="w-full pl-10 pr-4 py-2.5 bg-slate-50/50 border border-slate-200 rounded-xl text-sm focus:outline-none focus:border-indigo-500 focus:ring-4 focus:ring-indigo-100 transition-all text-slate-800 placeholder:text-slate-400"
                    placeholder="••••••••"
                    required={!isLogin}
                  />
                </div>
              </div>
            )}

            <button
              type="submit"
              disabled={isLoading}
              className="w-full mt-2 bg-indigo-600 hover:bg-indigo-700 active:bg-indigo-800 text-white py-2.5 rounded-xl font-medium text-sm transition-all duration-150 shadow-lg shadow-indigo-100 flex items-center justify-center disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isLoading ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : isLogin ? (
                "Sign In"
              ) : (
                "Create Account"
              )}
            </button>
          </form>

          <div className="mt-6 text-center">
            <button
              onClick={() => setIsLogin(!isLogin)}
              className="text-xs text-indigo-600 hover:text-indigo-700 font-semibold underline-offset-4 hover:underline transition-all"
            >
              {isLogin
                ? "Don't have an account? Sign up"
                : "Already have an account? Sign in"}
            </button>
          </div>

          <div className="mt-6 pt-5 border-t border-slate-100">
            <p className="text-[11px] font-bold uppercase tracking-widest text-slate-400 text-center mb-3">
              Quick Login Options
            </p>
            <div className="grid grid-cols-3 gap-2">
              <button
                onClick={() =>
                  handleDemoFill("admin@quickdash.dev", "password123", "admin")
                }
                className="group flex flex-col items-center justify-center p-2 bg-rose-50 hover:bg-rose-100 border border-rose-100/50 rounded-xl transition-all"
              >
                <span className="text-[11px] font-bold text-rose-700">
                  Admin
                </span>
              </button>
              <button
                onClick={() =>
                  handleDemoFill(
                    "courier@quickdash.dev",
                    "password123",
                    "courier",
                  )
                }
                className="group flex flex-col items-center justify-center p-2 bg-amber-50 hover:bg-amber-100 border border-amber-100/50 rounded-xl transition-all"
              >
                <span className="text-[11px] font-bold text-amber-700">
                  Courier
                </span>
              </button>
              <button
                onClick={() =>
                  handleDemoFill(
                    "customer@quickdash.dev",
                    "password123",
                    "customer",
                  )
                }
                className="group flex flex-col items-center justify-center p-2 bg-emerald-50 hover:bg-emerald-100 border border-emerald-100/50 rounded-xl transition-all"
              >
                <span className="text-[11px] font-bold text-emerald-700">
                  Customer
                </span>
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Login;
